/**
 * Channel attribution (features/booking-widget-embed.md R3). The shell PLUMBS a `channel` tag through
 * to the booking write path so reporting can tell an embed from a subdomain mount or a shared link — it
 * is analytics-grade and explicitly NOT a security boundary (design §2). The canonical `BookingChannel`
 * enum is OWNED by availability (DOMAIN.md §B.6: `SUBDOMAIN, EMBED, …`); the widget only carries the
 * channels a public booking can originate from and lets the API map them.
 */
export const WIDGET_CHANNELS = ["embed", "subdomain", "meta", "google", "qr"] as const;
export type WidgetChannel = (typeof WIDGET_CHANNELS)[number];

/** The canonical `BookingChannel` names the public write path accepts (DOMAIN.md §B.6). */
export type PublicBookingChannel = "EMBED" | "SUBDOMAIN" | "META" | "GOOGLE" | "QR";

/** Maps our carried channels onto the canonical `BookingChannel` names. */
export const BOOKING_CHANNEL: Record<WidgetChannel, PublicBookingChannel> = {
  embed: "EMBED",
  subdomain: "SUBDOMAIN",
  meta: "META",
  google: "GOOGLE",
  qr: "QR",
};

function isWidgetChannel(value: string | null): value is WidgetChannel {
  return (WIDGET_CHANNELS as readonly (string | null)[]).includes(value);
}

/**
 * Resolve the channel for this mount:
 *  - an explicit, VALID `?channel=` wins (the loader stamps `embed`; the island passes `subdomain`; a
 *    shared link carries `meta`, `google` or `qr`);
 *  - otherwise default by context — an island mount is `subdomain`, everything else (iframe embed AND
 *    a bare standalone visit) is `embed` (design §2: "embed (loader + standalone)").
 * An unknown/garbage value never leaks through — it falls back to the context default.
 */
export function resolveChannel(
  channelParam: string | null | undefined,
  context: "iframe" | "island" | "standalone",
): WidgetChannel {
  if (isWidgetChannel(channelParam ?? null)) return channelParam as WidgetChannel;
  return context === "island" ? "subdomain" : "embed";
}

/**
 * Parse the `preview` URL flag into a boolean (tolerant of `1`/`true`). Preview mode is the manager's
 * "Aperçu en direct": the booking journey renders and behaves exactly as live, but the final submit is
 * SIMULATED — no reservation is ever created — so a manager testing the embed can walk the whole flow
 * without booking a real table. It is analytics/UX-only, never a security boundary (the API still owns
 * every real write); a diner-facing embed simply never carries the flag.
 */
export function resolvePreview(raw: string | null | undefined): boolean {
  return raw === "1" || raw === "true";
}
